import { useServerRequest } from '../api'
import { type GetHookResponse } from '../hooks'
import { useQuery } from '@tanstack/react-query'
import {
  type APIError,
  messageFromError,
  HOUR,
  type SunrunOktaUser,
  isNotEmpty,
  type UserSunrunListSuccess,
  type UserSunrunListInput,
} from '@terros/common'
import { useState } from 'react'

export const useSunrunOktaUsers = (enabled = false): GetHookResponse<SunrunOktaUser[]> => {
  const serverRequest = useServerRequest<UserSunrunListInput, UserSunrunListSuccess>('/user/sunrun/list')
  const [users, setUsers] = useState<SunrunOktaUser[] | undefined>(undefined)

  const fetchAllFn = async (): Promise<SunrunOktaUser[]> => {
    let nextId: string | undefined = undefined
    let hasNextPage = true
    setUsers([])
    while (hasNextPage) {
      try {
        const result = await serverRequest({ nextId })
        setUsers((prev) => [...(prev ?? []), ...result.users])
        nextId = result.nextId
        hasNextPage = isNotEmpty(result.nextId) && isNotEmpty(result.users)
      } catch {}
    }
    return users ?? []
  }

  const query = useQuery<SunrunOktaUser[], APIError>({
    queryKey: ['users', 'sunrun-okta'],
    queryFn: fetchAllFn,
    retry: false,
    enabled,
    gcTime: Infinity,
    staleTime: HOUR,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const error = messageFromError(query.error)
  return { ...query, error, data: users, lastUpdatedAt: query.dataUpdatedAt }
}
